import React, { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';

import { Affiliation } from '@/types/affiliation';
import { useAppDispatch, RootState } from '@/store';
import { authStore } from '@/store/auth';
import { pages } from '@/settings/pages';

import { OrganizationsForSelect } from '../Presentation';
import { affiliationsApi } from '@/services/api';

import { CenteredLoader } from '@/components/utils/Loader';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import i18n from '@/settings/i18n';

const selector = ({ auth }: RootState) => ({
  hasSession: !!auth.session,
  organizationId: auth.current.organization.id,
  affiliations: [],
});

export const OrganizationsForSelectContainer: React.FC = () => {
  const { organizationId } = useSelector(selector);
  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);
  const [isLoadingAffiliations, setIsLoadingAffiliations] = useState(true);
  const { flashError } = useFlashMessenger();

  const dispatch = useAppDispatch();

  const linkTo =
    (to: string, withLocation = false) =>
    () => {
      if (withLocation) {
        navigate(to, { state: { from: location } });
      } else {
        navigate(to);
      }
    };

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from as string | undefined;

  // window.location.assignで遷移してきたため、queryからfromを取得
  const search = location.search;
  const query = new URLSearchParams(search);
  const from_login_page = query.get('from') === 'login' ? true : false;

  const fetchAffiliations = useCallback(async () => {
    try {
      const affiliations = await affiliationsApi.myOrganizations();
      if (affiliations.length === 1 && from_login_page) {
        dispatch(authStore.actions.selectOrganization(affiliations[0]));
        navigate(from || pages.top, { replace: true });
      }
      setAffiliations(affiliations);
      setIsLoadingAffiliations(false);
    } catch (err) {
      flashError(i18n.t('ui.error.unexpected'));
    }
  }, [dispatch, flashError, from, from_login_page, navigate]);

  useEffect(() => {
    fetchAffiliations();
  }, [fetchAffiliations]);

  const assignAffiliation = useCallback(
    (affiliation: Affiliation) => {
      dispatch(authStore.actions.selectOrganization(affiliation));
      navigate(from || pages.top, { replace: true });
    },
    [dispatch, from, navigate],
  );

  return isLoadingAffiliations ? (
    <CenteredLoader />
  ) : (
    <OrganizationsForSelect
      currentId={organizationId}
      affiliations={affiliations}
      onSelect={assignAffiliation}
      logout={linkTo(pages.signout.index)}
    />
  );
};
